import React, { useState, useContext, useEffect, useRef } from 'react';
import icons from '../../../icons';
import style from './Add.module.css';

import OptionSelection from './OptionSelection/OptionSelection';
import TextButton from '../../Buttons/TextButton/TextButton';

import { AppContext } from '../../../AppContext';

import { db } from '../../../firebase';
import { setDoc, updateDoc, doc, collection } from 'firebase/firestore';

import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { EffectFlip  } from 'swiper';
import 'swiper/swiper-bundle.css';

import { uuidv4 } from '@firebase/util';
import ErrorSnackbar from '../../ErrorSnackbar/ErrorSnackbar';

import { motion } from "framer-motion";


SwiperCore.use([ EffectFlip ]);

const gradients = [
    ["rgb(70, 70, 70)", "rgb(54, 54, 54)"],
    ["#f12711", "#f5af19"],
    ["#0575E6", "#021B79"],
    ["#45a247", "#283c86"],
    ["#2C3E50", "#FD746C"],
    ["#ACBB78", "#799F0C"],
    ["#ad5389", "#3c1053"],
    ["#833ab4", "#fd1d1d"],
    ["#5f2c82", "#49a09d"],
    ["#e9d362", "#333333"],
    ["#4ECDC4", "#556270"],
    ["#cbad6d", "#d53369"],
    ["#16A085", "#F4D03F"],
    ["#1a2a6c", "#b21f1f"]
]

const Add = props => {
    const { subjects, user, flashCards, updateFlashCards } = useContext(AppContext);

    const [selectingOption, setSelectingOption] = useState(null);
    const [type, setType] = useState(props.type === "flashcard" ? "flashcard" : "category");
    const [name, setName] = useState(props.name ?? "");
    const [subject, setSubject] = useState(props.subject);
    const [color, setColor] = useState(props.color ?? gradients[0]);

    const [question, setQuestion] = useState(props.question ?? "");
    const [answer, setAnswer] = useState(props.answer ?? "");
    const [category, setCategory] = useState(props.category?.id ?? "");

    const [error, setError] = useState({ show: false, error: "" });
    const [saving, setSaving] = useState(false);

    const questionSwiperRef = useRef();

    const getSubjectIcon = subjectName => {
        let returnIcon = "question_mark_orange_icon";

        if (subjects)
            Object.values(subjects).forEach(subject => {
                if (subject.subject?.toLowerCase() === subjectName?.toLowerCase()) returnIcon = subject.icon;
            });

        return returnIcon;
    };

    const save = async () => {
        if ((type === "category" && (!name || !subject)) || (type === "flashcard" && (!category || !question || !answer)) || saving) {
            setError({ show: true, error: "Riempi tutti i campi!" });
            return;
        }

        setSaving(true);

        if (type === "category") {
            const docRef = doc(collection(db, `flashcards`));

            const newCategory = {
                id: props.id ?? docRef.id,
                author: user.id,
                name,
                color,
                subject,
                active: flashCards[props.id]?.active ?? false,
                flashCards: flashCards[props.id]?.flashCards ?? {},
                createdAt: new Date().toString(),
            };

            await setDoc(doc(db, `flashcards/${newCategory.id}`), newCategory);
            updateFlashCards({ [newCategory.id]: newCategory });
        } else if (type === "flashcard") {
            let id = uuidv4();
            while (Object.keys(flashCards[category]).includes(id))
                id = uuidv4();

            const newFlashCard = {
                id: props.id ?? id,
                author: user.id,
                question,
                answer,
                color,
                box: 1,
                lastDone: new Date(1970, 0, 1) 
            };

            const oldCategory = flashCards[category];
            await updateDoc(doc(db, `flashcards/${category}`), { [`flashCards.${newFlashCard.id}`]: newFlashCard })
            updateFlashCards({ [category]: { ...oldCategory, flashCards: { ...oldCategory.flashCards, [newFlashCard.id]: newFlashCard } } });
        }

        setSaving(false);
        props.hide();
    };

    const resetState = () => {
        setType("category");
        setName("");
        setSubject(null);
        setQuestion("");
        setAnswer("");
        setCategory(null);
        setSaving(false);
        setColor(gradients[0]);
    };

    useEffect(() => {
        setType(props.type);
        if (props.id && props.type === "category") {
            setName(flashCards[props.id].name);
            setColor(flashCards[props.id].color);
            setSubject(flashCards[props.id].subject);
        } else if (props.type === "flashcard") {
            setAnswer(props.answer);
            setQuestion(props.question);
            setColor(props.color);
            setCategory(props.category);
        }
    }, [props.id, props.type, props.category, props.flashCards]);

    return <>
        <OptionSelection 
            option={ selectingOption }
            selectedOptions={{
                type,
                subject,
                category,
            }}
            value={ 
                selectingOption === "type" ? type :  
                selectingOption === "subject" ? subject :  
                selectingOption === "category" ? category : null  
            }
            back={ () => setSelectingOption(null) }
            select={ value => {
                switch (selectingOption) {
                    case "type":
                        setType(value);
                        break;
                    case "subject":
                        setSubject(value);
                        break;
                    case "category":
                        setCategory(value);
                        break;
                };

                setSelectingOption(null);
            } } 
        />
        <main className={ style.main }>
            <ErrorSnackbar error={ error.error } show={ error.show } setShow={ show => setError(oldError => ({ ...oldError, show })) } />
            <div className={ style.flex } style={{ marginLeft: "1rem" }}>
                <img src={ icons.arrow_left_icon } className={ style.backIcon } onClick={ () => {
                    resetState();
                    props.setAddProps({
                        type: "category",
                        name: "",
                        subject: null,
                        question: "",
                        answer: "",
                        category: null,
                        saving: false,
                        color: gradients[0],
                    });
                    props.hide();
                } }></img>
                { props.id ? <h2>Modifica<br></br><span style={{ color: "var(--orange)" }}>{ type === "category" ? "Categoria" : "Flashcard" }</span></h2> : <h2>Aggiungi<br></br>Nuova <span style={{ color: "var(--orange)" }}>{ type === "category" ? "Categoria" : "Flashcard" }</span></h2> }
            </div>
            <div className={ style.detailsContainer }>
                <div className={ style.detail } onClick={ () => setSelectingOption("type") }>
                    <img src={ type === "category" ? icons.test_icon : icons.flashcards_icon }></img>
                    <div>
                        <h4 className={ style.detailName }>Tipo</h4>
                        <h4 className={ style.detailValue }>{ type === "category" ? "Categoria" : "Flashcard" }</h4>
                    </div>
                </div>
                { type === "flashcard" ?   
                    <div className={ style.detail } onClick={ () => setSelectingOption("category") }>
                        <img src={ icons[getSubjectIcon(flashCards[category]?.subject)] }></img>
                        <div>
                            <h4 className={ style.detailName }>Categoria</h4>
                            <h4 className={ style.detailValue }>{ flashCards[category]?.name ?? "Scegli" }</h4>
                        </div>
                    </div> :
                    <div className={ style.detail } onClick={ () => setSelectingOption("subject") }>
                        <img src={ icons[getSubjectIcon(subject)] }></img>
                        <div>
                            <h4 className={ style.detailName }>Materia</h4>
                            <h4 className={ style.detailValue }>{ subject ?? "Scegli" }</h4>
                        </div>
                    </div>
                }
            </div>
            { type === "category" ? <>
                    <h5 className={ style.subHeader }>Categoria</h5>
                    <div className={ style.card } style={{ marginLeft: "1rem", marginRight: "1rem", marginTop: "1rem", background: color ? `linear-gradient(to bottom right, ${color[0]}, ${color[1]})` : null }}>
                        <input className={ style.cardInput } style={{ margin: "auto", display: "block" }} value={ name } onChange={ e => setName(e.target.value ? (e.target.value[0].toUpperCase() + e.target.value.substring(1)) : "") }></input>
                        {/* <textarea className={ style.description } value={ description } onChange={ e => setDescription(e.target.value) } style={{ marginBottom: "1rem" }}></textarea> */}
                    </div>
                </> : <div style={{ position: "relative" }}>
                    <Swiper ref={ questionSwiperRef } flipEffect={ true } effect="flip" loop={ true } style={{ marginLeft: "1rem", marginRight: "1rem", width: "calc(100vw - 2rem)" }}>
                        <SwiperSlide>
                            <img src={ icons.loop_orange_icon } style={{ position: "absolute", top: "1.5rem", right: "0", width: "1.25rem", height: "1.25rem", zIndex: "2" }} onClick={ () => questionSwiperRef.current.swiper.slideTo(questionSwiperRef.current.swiper.activeIndex === 0 ? 1 : 0) }></img>
                            <h5 className={ style.subHeader } style={{ marginLeft: "0" }}>Domanda</h5>
                            <div className={ style.card } style={{ background: color ? `linear-gradient(to bottom right, ${color[0]}, ${color[1]})` : null }}>
                                <input className={ style.cardInput } style={{ margin: "auto", display: "block" }} value={ question } onChange={ e => setQuestion(e.target.value ? (e.target.value[0].toUpperCase() + e.target.value.substring(1)) : "") }></input>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <img src={ icons.loop_orange_icon } style={{ position: "absolute", top: "1.5rem", right: "0", width: "1.25rem", height: "1.25rem", zIndex: "2" }} onClick={ () => questionSwiperRef.current.swiper.slideTo(questionSwiperRef.current.swiper.activeIndex === 0 ? 1 : 0) }></img>
                            <h5 className={ style.subHeader } style={{ marginLeft: "0" }}>Risposta</h5>
                            <div className={ style.card } style={{ background: color ? `linear-gradient(to bottom right, ${color[0]}, ${color[1]})` : null }}>
                                <input className={ style.cardInput } style={{ margin: "auto", display: "block" }} value={ answer } onChange={ e => setAnswer(e.target.value ? (e.target.value[0].toUpperCase() + e.target.value.substring(1)) : "") }></input>
                            </div>
                        </SwiperSlide>
                    </Swiper>
                    <motion.h6 animate={{ height: question ? null : 0 }} style={{  overflowY: "hidden", marginLeft: "1rem", marginRight: "1rem", textAlign: "center", fontSize: ".75rem", marginTop: "1rem", marginBottom: "0", color: "rgb(145, 143, 143)" }}>Fai swipe per girare la carta e scrivere la risposta</motion.h6>
                </div> }
            { type === "flashcard" && <>
                <h5 className={ style.subHeader }>Colore</h5>
                <div className={ style.colorChoices }>
                    { gradients.map(([from , to]) => <div key={ `color-${from + to}` } className={ [style.colorChoice, (color[0] === from && color[1] === to) && style.selected].join(" ") } style={{ background: `linear-gradient(${from}, ${to})` }} onClick={ () => setColor([from, to]) }></div>) }
                </div>
            </> }
            <TextButton loading={ saving } text="SALVA" style={{ width: "70vw", marginLeft: "auto", marginRight: "auto", marginTop: "2rem", marginBottom: "1rem", padding: ".25rem 0" }} fontSize="1.1rem" onClick={ save } />
            {/* { oldType && <TextButton loading={ saving } text="ELIMINA" style={{ background: "linear-gradient(to left, rgb(150, 1, 1), rgb(255, 8, 0)", width: "70vw", marginLeft: "auto", marginRight: "auto", marginTop: "1rem", marginBottom: "2rem", padding: ".25rem 0" }} fontSize="1.1rem" onClick={ deleteEvent } /> } */}
        </main>
    </>
};

export default Add;